import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { Box, IconButton, Typography, useTheme } from '@mui/material';
import { ArrowCheckIcon } from '@sharedCR/coreDesignSystem/icons';
import Skeleton from 'app/shared/components/common/Skeleton';
import clsx from 'clsx';
import withLoading, { WithLoadingPropTypes } from 'hocs/withLoading';
import { compose } from 'redux';

import { IconDirection } from 'constants/coreDesignSystem';
import { SKELETON_TYPES } from 'constants/skeleton';
import useVisibility from 'hooks/useVisibility';
import { getScrollStyles } from 'styles/scrollbar.styles';

import { selectBorrowersContacts } from 'store/loanApplications/selectors';

import { getStyles } from '../Activities.styles';
import { ActivitiesTestIds } from '../Activities.testIds';
import useNewEmailModal from '../hooks/useNewEmailModal';

import ContactItem from './ContactItem';

const ContactsColumn = ({ isLoading }) => {
  const theme = useTheme();
  const scrollClasses = getScrollStyles(theme);

  const [isCollapsed, setIsCollapsed] = useState(false);
  const classes = getStyles(theme, { isCollapsed });

  const contactsRef = useRef();
  const plugRef = useRef();

  const borrowersContacts = useSelector(selectBorrowersContacts);

  const [borrower, ...coBorrowers] = borrowersContacts;

  const [isInContactsColumnBottom, setIsInContactsColumnBottom] = useState(false);

  const { onContactBorrower } = useNewEmailModal();

  const scrollToTop = useCallback(() => {
    const scrollContainer = contactsRef.current;

    if (scrollContainer) {
      scrollContainer.scrollIntoView({ block: 'center' });
    }
  }, []);

  const isContactsVisible = useVisibility(contactsRef, {}, { once: false });
  const isPlugVisible = useVisibility(plugRef, {}, { once: false });

  useEffect(() => {
    setIsInContactsColumnBottom(isPlugVisible);
  }, [isPlugVisible]);

  useEffect(() => {
    if (!isContactsVisible && !isCollapsed) {
      setIsCollapsed(true);
    }

    if (isContactsVisible && isCollapsed) {
      setIsCollapsed(false);
    }
  }, [isContactsVisible, isCollapsed]);

  return (
    <Box className={clsx(isCollapsed ? 'max-w-48' : '', 'relative')} sx={classes.contactsColumn}>
      <div ref={contactsRef}>
        {isLoading ? (
          <Skeleton length={4} type={SKELETON_TYPES.LIST} />
        ) : (
          <>
            {!isCollapsed && (
              <Box className="p-10" sx={classes.borrowerBlock}>
                <Typography variant="medium14">Borrower:</Typography>
                <ContactItem {...borrower} onClickEmail={onContactBorrower} />
              </Box>
            )}
            {!!coBorrowers.length && !isCollapsed && (
              <Box className="overflow-y-auto p-10" sx={scrollClasses.scroll}>
                <Typography variant="medium14">Co-Borrower(s):</Typography>
                {coBorrowers.map((coBorrower, index) => (
                  <ContactItem key={index} {...coBorrower} onClickEmail={onContactBorrower} />
                ))}
              </Box>
            )}
          </>
        )}
      </div>
      {isCollapsed && (
        <IconButton
          className={clsx(
            'rounded-none',
            isInContactsColumnBottom ? 'absolute bottom-0' : 'fixed bottom-0',
          )}
          data-testid={ActivitiesTestIds.CONTACTS_SCROLL_TOP_BUTTON}
          onClick={scrollToTop}
        >
          <ArrowCheckIcon direction={IconDirection.UP} />
        </IconButton>
      )}

      {/* to adjust IconButton position while scroll */}
      <div className="absolute bottom-0 h-48 w-full -z-10" ref={plugRef} />
    </Box>
  );
};

ContactsColumn.propTypes = {
  ...WithLoadingPropTypes,
};

export default compose(withLoading, memo)(ContactsColumn);
